import * as $script from 'loadjs';
import { Subject } from 'rxjs';
import { get } from 'lodash';

// services
import { Service as AnalyticsService } from 'modules/analytics-provider';

// constants
import {
    HEADER_HEIGHT,
    INTEGRATOR_TRIGGER_USER_DATA,
    INTEGRATOR_TRIGGER_LOGOUT
} from './constants';

// utils
import { getEmbeddedOverlay, getOverlaySDKEndpoint } from './utils';
import { isEmbedded } from 'utils/css';
import { OverlayServiceMock } from './service.mock';

// types
import {
    IConnectSDK,
    IGetProfilesResponse,
    IGetTicketResponse,
    IGetThirdPartyStatusResponse,
    IGetUserStatusResponse,
    IGeneralUserData
} from 'modules/ubisoft-connect-service/types';
import {
    IUbiOverlayServiceConfig,
    IUbiOverlayWidget,
    IUbiOverlayWidgetConfig,
    IWindowWithOverlay
} from 'modules/ubisoft-overlay-service/types';
import { WEBSITE_MODE } from '@sitegen/common/constants/config';

export class UbisoftOverlayService {
    public overlaySDK: IUbiOverlayWidget;
    private overlayConnectSDK: IConnectSDK;
    private config: IUbiOverlayWidgetConfig;
    private onInitCallback: Function;

    public overlayLoginTriggerSubject: Subject<undefined> = new Subject();
    public overlayLogoutTriggerSubject: Subject<undefined> = new Subject();

    public install = async ({ mode, envName }: {
        mode: WEBSITE_MODE,
        envName: string
    },
        onSuccess: Function,
        onInitDone: Function,
    ) => {
        window.addEventListener('message', this.receiveUbisoftOverlayMessage, false);
        this.onInitCallback = onInitDone;

        return isEmbedded(mode)
            ? await this.initEmbeddedFlow(onSuccess, onInitDone)
            : await UbisoftOverlayService.loadSDKScript(envName, onSuccess);
    };

    public init(containerSelector: string) {
        if (!containerSelector) {
            return;
        }

        const { overlayWidget } = window as IWindowWithOverlay;
        this.overlaySDK = overlayWidget;

        const options = {
            ...this.config,
            container: `#${containerSelector}`,
        };

        return this.overlaySDK.init(options);
    }

    public initEmbeddedFlow(onInstallDone: Function, onInitDone: Function): Promise<void> {
        return new Promise((resolve, onError) => {
            const onSuccess = () => {
                const { overlayWidget, overlayConnectSDK } = window as IWindowWithOverlay;
                this.overlaySDK = overlayWidget;
                this.overlayConnectSDK = overlayConnectSDK;
                onInstallDone();
                onInitDone();
                return resolve();
            };

            return getEmbeddedOverlay(onError, onSuccess);
        });
    }

    public configure(config: IUbiOverlayServiceConfig) {
        this.config = {
            appId: config.appId,
            genomeId: config.genomeId,
            locale: config.locale,
            loginType: 'modal',
            height: HEADER_HEIGHT,
            showSignInText: 'DEFAULT',
            onConnect: (action: string, response: IGetProfilesResponse) => {
                if (action === 'init') {
                    const userId = get(response, 'payload.userId');
                    const hasLoginSession = Boolean(response.status === 'ok' && userId);

                    AnalyticsService.triggerInitialPageEvents({ userId, hasLoginSession });

                    this.overlayConnectSDK = (window as IWindowWithOverlay).overlayConnectSDK;
                    this.onInitCallback();

                    if (hasLoginSession) {
                        return this.overlayLoginTriggerSubject.next();
                    }
                }
            }
        };
    }

    public getConfig() {
        return this.config;
    }

    public reInit() {
        if (this.overlaySDK) {
            this.overlaySDK.reInit();
        }
    }

    public static changeLocale(locale: string) {
        const { overlayWidget } = window as IWindowWithOverlay;
        if (!get(overlayWidget, 'hasInitialized', false)) {
            return;
        }

        return overlayWidget.changeLocale(locale);
    }

    public static loadSDKScript(envName: string, callback: Function): Promise<void> {
        return new Promise<void>(resolve => {
            if ((window as IWindowWithOverlay).overlayWidget) {
                return resolve(callback());
            }

            const scriptId = 'overlaySDK';
            const scriptUrl = getOverlaySDKEndpoint(envName);

            $script(scriptUrl, scriptId);
            $script.ready(scriptId, function () {
                return resolve(callback());
            });
        });
    }

    public receiveUbisoftOverlayMessage = (event: MessageEvent) => {
        if (!this.overlaySDK) {
            return;
        }

        if (this.isLoginEvent(event)) {
            return this.overlayLoginTriggerSubject.next();
        }

        if (this.isLogoutEvent(event)) {
            return this.overlayLogoutTriggerSubject.next();
        }
    };

    public isLoginEvent(event: MessageEvent) {
        return event.data.topic === INTEGRATOR_TRIGGER_USER_DATA;
    }

    public isLogoutEvent(event: MessageEvent) {
        return event.data.topic === INTEGRATOR_TRIGGER_LOGOUT;
    }

    public openLoginPopup() {
        return this.overlaySDK.login();
    }

    public getTicket() {
        return new Promise<{
            ticket: string,
            sessionId: string,
            userId: string
        }>((resolve, reject) => {
            if (!this.overlaySDK.hasInitialized) {
                return reject();
            }

            return this.overlaySDK.getTicket((getTicketResponse: IGetTicketResponse) => {
                if (getTicketResponse.status !== 'ok') {
                    return reject();
                }

                const { payload: { ticket, userId, sessionId } } = getTicketResponse;
                return resolve({ ticket, userId, sessionId });
            });
        });
    }

    public getProfiles() {
        return new Promise<IGetProfilesResponse>((resolve, reject) => {
            return this.overlaySDK.getProfiles((getProfilesResponse: IGetProfilesResponse) => {
                if (getProfilesResponse.status !== 'ok') {
                    return this.overlayConnectSDK
                        ? this.getProfilesFallback(resolve, reject)
                        : reject();
                }

                return resolve(getProfilesResponse);
            });
        });
    }

    public getProfilesFallback = async (resolve: Function, reject: Function) => {
        let gotProfiles = false;
        const timeoutId = setTimeout(function() {
            if (!gotProfiles) {
                console.warn('[Ubisoft Overlay] Get profiles timeout.');
                return reject();
            }
        }, 10 * 1000);

        try {
            const { ticket, userId, sessionId } = await this.getTicket();

            return this.overlayConnectSDK
                .getProfiles(ticket, sessionId, userId)
                .subscribe((getProfilesResponse: IGetProfilesResponse) => {
                    gotProfiles = true;
                    clearTimeout(timeoutId);
                    return resolve(getProfilesResponse);
                });
        } catch {
            return reject();
        }
    };

    public triggerEmailActivation(callback: Function) {
        return this.overlaySDK.triggerEmailActivation(callback);
    }

    public getUserStatus(userData: IGeneralUserData, callback: Function) {
        return this.overlaySDK.getUserStatus((response: IGetUserStatusResponse) => response.status === 'ok'
            ? callback(response)
            : this.overlayConnectSDK.getUserStatus(userData.ticket, userData.sessionId, userData.userId).subscribe(callback));
    }

    public getThirdPartyStatus() {
        return new Promise<boolean>((resolve, reject) => {
            let gotTPS = false;
            const timeoutId = setTimeout(function() {
                if (!gotTPS) {
                    console.warn('[Ubisoft Overlay] getthirdPartyStatus timeout.');
                    return reject();
                }
            }, 10 * 1000);

            return this.overlayConnectSDK.getThirdPartyStatus()
                .subscribe((getThirdPartyStatusResponse: IGetThirdPartyStatusResponse) => {
                    gotTPS = true;
                    clearTimeout(timeoutId);

                    return resolve(getThirdPartyStatusResponse.status === 'ok');
                });
        });
    }

    public logout(ticket: string, sessionId: string) {
        return new Promise<void>((resolve, _reject) => {
            this.overlayConnectSDK.logout(ticket, sessionId);
            return resolve();
        });
    }

    public configureFakeAPI = () => {
        UbisoftOverlayService.changeLocale = OverlayServiceMock.changeLocale;

        this.init = OverlayServiceMock.init;
        this.reInit = OverlayServiceMock.reInit;
        this.getTicket = OverlayServiceMock.getTicket;
        this.getProfiles = OverlayServiceMock.getProfiles;
        this.getUserStatus = OverlayServiceMock.getUserStatus;
        this.openLoginPopup = OverlayServiceMock.openLoginPopup;
        this.getThirdPartyStatus = OverlayServiceMock.getThirdPartyStatus;
        this.triggerEmailActivation = OverlayServiceMock.triggerEmailActivation;
    };
}

const ubisoftOverlayService = new UbisoftOverlayService();

export default ubisoftOverlayService;
